import { cn } from '@lib/util/cn';
import type React from 'react';
import OptionSelectDropdown from './option-select-dropdown';

const OPTION_DROPDOWN_THRESHOLD = 6;

type ProductOptionShape = {
  id: string;
  values?: Array<{ value: string }> | null;
};

type OptionSelectProps = {
  option: ProductOptionShape;
  current: string | undefined;
  updateOption: (title: string, value: string) => void;
  title: string;
  disabled: boolean;
  'data-testid'?: string;
};

const sortOptionValues = (values: string[]): string[] =>
  [...values].sort((a, b) => {
    const tokenize = (s: string) =>
      s.match(/(\d+(\.\d+)?)|([^\d]+)/g)?.map((t) => {
        const n = parseFloat(t);
        return Number.isNaN(n) ? t.toLowerCase() : n;
      }) ?? [];

    const ta = tokenize(a);
    const tb = tokenize(b);
    for (let i = 0; i < Math.min(ta.length, tb.length); i++) {
      const x = ta[i];
      const y = tb[i];
      if (typeof x === 'number' && typeof y === 'number' && x !== y)
        return x - y;
      if (typeof x === 'string' && typeof y === 'string') {
        const c = x.localeCompare(y, undefined, { sensitivity: 'base' });
        if (c !== 0) return c;
      }
    }
    return ta.length - tb.length;
  });

const OptionSelect: React.FC<OptionSelectProps> = ({
  option,
  current,
  updateOption,
  title,
  'data-testid': dataTestId,
  disabled,
}) => {
  const sortedValues = sortOptionValues(
    (option.values ?? []).map((v) => v.value),
  );

  if (sortedValues.length > OPTION_DROPDOWN_THRESHOLD) {
    return (
      <OptionSelectDropdown
        optionId={option.id}
        title={title}
        values={sortedValues}
        current={current}
        updateOption={updateOption}
        disabled={disabled}
        data-testid={dataTestId}
      />
    );
  }

  return (
    <div className="flex flex-col gap-y-1">
      <span className="text-sm text-brand-primary-800" translate="no">
        {title === 'Variant' ? 'Select Option' : title}
      </span>
      <div
        className="flex flex-wrap justify-between gap-2 items-stretch"
        data-testid={dataTestId}
      >
        {sortedValues.map((v) => (
          <button
            type="button"
            onClick={(e) => {
              updateOption(option.id, v);
              e.currentTarget.blur();
            }}
            key={v}
            className={cn(
              'border-ui-border-base cursor-pointer bg-ui-bg-subtle border typography-paragraph rounded-rounded px-3 py-2 flex-1 transition-all duration-150 text-center',
              {
                'border-fg-interactive-strong bg-surface-accent-subtle text-fg-interactive-soft':
                  v === current,
                'hover:shadow-elevation-card-rest hover:border-gray-300':
                  v !== current,
              },
            )}
            disabled={disabled}
            data-testid="option-button"
            translate="no"
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
};

export default OptionSelect;
